import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../foundation/altzero_base/base/components/ui/tabs';
import { Input } from '../../../foundation/altzero_base/base/components/ui/input';
import { Label } from '../../../foundation/altzero_base/base/components/ui/label';
import { Card, CardContent } from '../../../foundation/altzero_base/base/components/ui/card';
import { RateTypeData, MultiYearRates, EnvironmentalCharges, NonEnergyCharges, EnvironmentalChargeYearData } from '../types/offerTypes';
import { Zap, Leaf, DollarSign } from 'lucide-react';
import { ENERGY_RATE_STEP, ENV_RATE_STEP } from '../lib/ratePrecision';
import { formatRateYearLabel } from '../lib/termLabel';

interface DetailedRateInputProps {
    data: RateTypeData;
    onChange: (data: RateTypeData) => void;
}

const DetailedRateInput: React.FC<DetailedRateInputProps> = ({ data, onChange }) => {

    const updatePeakRates = (year: keyof MultiYearRates, value: string) => {
        const numValue = parseFloat(value) || 0;
        onChange({
            ...data,
            peakRates: { ...data.peakRates, [year]: numValue }
        });
    };

    const updateOffPeakRates = (year: keyof MultiYearRates, value: string) => {
        const numValue = parseFloat(value) || 0;
        onChange({
            ...data,
            offpeakRates: { ...data.offpeakRates, [year]: numValue }
        });
    };

    const updateShoulderRates = (year: keyof MultiYearRates, value: string) => {
        const numValue = parseFloat(value) || 0;
        onChange({
            ...data,
            shoulderRates: { ...data.shoulderRates, [year]: numValue }
        });
    };

    const updateNonEnergyCharge = (key: string, value: string) => {
        const numValue = parseFloat(value) || 0;
        onChange({
            ...data,
            nonEnergyCharges: { ...data.nonEnergyCharges, [key]: numValue }
        });
    };

    const updateEnvironmentalCharge = (
        type: keyof EnvironmentalCharges,
        year: keyof EnvironmentalCharges['LREC'],
        field: keyof EnvironmentalChargeYearData,
        value: string
    ) => {
        const numValue = parseFloat(value) || 0;
        const currentTypeData = data.environmentalCharges[type];
        const currentYearData = currentTypeData[year];

        onChange({
            ...data,
            environmentalCharges: {
                ...data.environmentalCharges,
                [type]: {
                    ...currentTypeData,
                    [year]: {
                        ...currentYearData,
                        [field]: numValue
                    }
                }
            }
        });
    };

    const years: (keyof MultiYearRates)[] = ['year_1', 'year_2', 'year_3', 'year_4'];

    return (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <Tabs defaultValue="energy" className="w-full">
                <div className="bg-gray-50 border-b border-gray-200 px-4 py-2">
                    <TabsList className="grid w-full grid-cols-3 h-9">
                        <TabsTrigger value="energy" className="text-xs">Energy Rates</TabsTrigger>
                        <TabsTrigger value="env" className="text-xs">Environmental</TabsTrigger>
                        <TabsTrigger value="non-energy" className="text-xs">Non-Energy</TabsTrigger>
                    </TabsList>
                </div>

                <div className="p-4 max-h-[400px] overflow-y-auto custom-scrollbar">
                    {/* Energy Rates Tab */}
                    <TabsContent value="energy" className="mt-0 space-y-6">
                        <div className="space-y-4">
                            <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                <Zap className="w-4 h-4 text-orange-500" /> Energy Rates (c/kWh)
                            </h4>

                            <div className="grid grid-cols-4 gap-2 mb-2">
                                <div className="text-[10px] font-medium text-gray-500 uppercase">Year</div>
                                <div className="text-[10px] font-medium text-gray-500 uppercase">Peak</div>
                                <div className="text-[10px] font-medium text-gray-500 uppercase">Off-Peak</div>
                                <div className="text-[10px] font-medium text-gray-500 uppercase">Shoulder</div>
                            </div>

                            {years.map((year) => (
                                <div key={year} className="grid grid-cols-4 gap-2 items-center">
                                    <div className="text-xs font-medium text-gray-700 capitalize">
                                        {formatRateYearLabel(year)}
                                    </div>
                                    <Input
                                        type="number"
                                        step={ENERGY_RATE_STEP}
                                        className="h-8 text-xs"
                                        placeholder="0.0000"
                                        value={data.peakRates[year] || ''}
                                        onChange={(e) => updatePeakRates(year, e.target.value)}
                                    />
                                    <Input
                                        type="number"
                                        step={ENERGY_RATE_STEP}
                                        className="h-8 text-xs"
                                        placeholder="0.0000"
                                        value={data.offpeakRates[year] || ''}
                                        onChange={(e) => updateOffPeakRates(year, e.target.value)}
                                    />
                                    <Input
                                        type="number"
                                        step={ENERGY_RATE_STEP}
                                        className="h-8 text-xs"
                                        placeholder="0.0000"
                                        value={data.shoulderRates[year] || ''}
                                        onChange={(e) => updateShoulderRates(year, e.target.value)}
                                    />
                                </div>
                            ))}
                        </div>
                    </TabsContent>

                    {/* Environmental Charges Tab */}
                    <TabsContent value="env" className="mt-0 space-y-6">
                        <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                            <Leaf className="w-4 h-4 text-green-500" /> Environmental Charges
                        </h4>

                        {(['LREC', 'SREC', 'VEEC/ESS/REES'] as const).map((type) => (
                            <Card key={type} className="border-green-100 shadow-sm overflow-hidden">
                                <div className="bg-green-50/50 border-b border-green-100 px-3 py-2">
                                    <h5 className="text-xs font-bold text-green-800 uppercase tracking-wider">{type}</h5>
                                </div>
                                <CardContent className="p-3 space-y-3">
                                    <div className="grid grid-cols-4 gap-2">
                                        <div className="text-[10px] font-medium text-gray-500">Year</div>
                                        <div className="text-[10px] font-medium text-gray-500 text-right">Rate/kWh</div>
                                        <div className="text-[10px] font-medium text-gray-500 text-right">Power %</div>
                                        <div className="text-[10px] font-medium text-gray-500 text-right">Cert. Price</div>
                                    </div>
                                    {years.map((year) => (
                                        <div key={year} className="grid grid-cols-4 gap-2 items-center">
                                            <div className="text-[10px] font-medium text-gray-700 capitalize">
                                                {formatRateYearLabel(year)}
                                            </div>
                                            <Input
                                                type="number"
                                                step={ENV_RATE_STEP}
                                                className="h-7 text-[10px] text-right px-1"
                                                placeholder="0.00"
                                                value={data.environmentalCharges[type][year].ratePerKwh ?? ''}
                                                onChange={(e) => updateEnvironmentalCharge(type, year, 'ratePerKwh', e.target.value)}
                                            />
                                            <Input
                                                type="number"
                                                step="0.01"
                                                className="h-7 text-[10px] text-right px-1"
                                                placeholder="0.00"
                                                value={data.environmentalCharges[type][year].powerPercentage ?? ''}
                                                onChange={(e) => updateEnvironmentalCharge(type, year, 'powerPercentage', e.target.value)}
                                            />
                                            <Input
                                                type="number"
                                                step="0.01"
                                                className="h-7 text-[10px] text-right px-1"
                                                placeholder="0.00"
                                                value={data.environmentalCharges[type][year].pricePerCertificate ?? ''}
                                                onChange={(e) => updateEnvironmentalCharge(type, year, 'pricePerCertificate', e.target.value)}
                                            />
                                        </div>
                                    ))}
                                </CardContent>
                            </Card>
                        ))}
                    </TabsContent>

                    {/* Non-Energy Charges Tab */}
                    <TabsContent value="non-energy" className="mt-0 space-y-6">
                        <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-blue-500" /> Non-Energy Charges
                        </h4>

                        <div className="space-y-3">
                            {['Meter Agreement', 'Network Service', 'Supplementary Metering', 'Market Fees'].map((charge) => (
                                <div key={charge} className="space-y-1">
                                    <Label className="text-xs text-gray-600">{charge}</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        className="h-9 text-sm"
                                        placeholder="$0.00"
                                        value={data.nonEnergyCharges[charge] || ''}
                                        onChange={(e) => updateNonEnergyCharge(charge, e.target.value)}
                                    />
                                </div>
                            ))}
                        </div>
                    </TabsContent>
                </div>
            </Tabs>
        </div>
    );
};

export default DetailedRateInput;
